import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
	useValidateClassCodeMutation,
	useVerifyIdentityMutation,
	useStudentSetPinMutation,
	useStudentLoginMutation,
	useStudentFaceLoginMutation,
	useListPublicSchoolsQuery,
	useLazyListPublicClassesQuery,
} from '../../store/api/authApi';
import { useAppDispatch } from '../../store/hooks';
import { setCredentials } from '../../store/authSlice';
import { PinPad } from '../../components/shared/PinPad';
import {
	ArrowRight,
	CheckCircle,
	Copy,
	ScanFace,
	Check,
	ChevronsUpDown,
	Search,
} from 'lucide-react';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '../../components/ui/popover';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '../../components/ui/command';
import { ButtonSpinner } from '../../components/shared/SharedComponents';
import { analytics, identifyUser } from '../../services/analytics';
import FaceCapture from '../../components/student/FaceCapture';
import { usePageView } from '../../hooks/usePageView';
import { EVENTS } from '../../constants/analyticsEvents';
import alforaaFullLogoDark from '../../assets/svg/logo/alforaa_full_logo_dark.svg';

const STEPS = { CLASS_CODE: 0, VERIFY: 1, SET_PIN: 2, REVEAL_ID: 3, LOGIN: 4 };

function AuroraInput({
	value,
	onChange,
	placeholder,
	type = 'text',
	className = '',
	loading = false,
	disabled,
	...rest
}: any) {
	return (
		<div className='relative'>
			<input
				type={type}
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				disabled={disabled || loading}
				className={`w-full px-4 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 text-[14px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/30 focus:border-aurora-coral disabled:bg-[#F4F4F8] disabled:text-aurora-ink-mute disabled:cursor-not-allowed shadow-[0_1px_2px_rgba(15,18,53,0.05)] transition-all ${loading ? 'pr-10' : ''} ${className}`}
				{...rest}
			/>
			{loading && (
				<span className='absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none'>
					<span
						className='inline-block w-4 h-4 rounded-full animate-spin'
						style={{
							border: '2px solid rgba(232,84,31,0.25)',
							borderTopColor: '#E8541F',
						}}
					/>
				</span>
			)}
		</div>
	);
}

export default function StudentLoginPage() {
	usePageView(EVENTS.student.LOGIN_PAGE_VIEWED);
	const navigate = useNavigate();
	useAuth();
	const dispatch = useAppDispatch();

	const [tab, setTab] = useState<'pin' | 'face'>('face');
	const [step, setStep] = useState(STEPS.LOGIN);
	const [classCode, setClassCode] = useState('');
	const [name, setName] = useState('');
	const [rollNumber, setRollNumber] = useState('');
	const savedPrefix = localStorage.getItem('studentIdPrefix');
	const [alforaaId, setAlforaaId] = useState(
		savedPrefix ? savedPrefix + '/' : '',
	);
	const [studentId, setStudentId] = useState('');
	const [studentData, setStudentData] = useState<any>(null);
	const [error, setError] = useState('');
	const [pinError, setPinError] = useState('');
	const [copied, setCopied] = useState(false);

	const savedSchoolId = localStorage.getItem('faceLoginSchoolId') || '';
	const savedSchoolName = localStorage.getItem('faceLoginSchoolName') || '';
	const [selectedSchoolId, setSelectedSchoolId] = useState(savedSchoolId);
	const [, setSelectedSchoolName] = useState(savedSchoolName);
	const [selectedClassId, setSelectedClassId] = useState('');
	const [, setSelectedClassCode] = useState('');
	const [showFaceScan, setShowFaceScan] = useState(false);
	const [faceError, setFaceError] = useState('');
	const [classComboOpen, setClassComboOpen] = useState(false);
	const [schoolSearch, setSchoolSearch] = useState(savedSchoolName);
	const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
	const schoolRef = useRef<HTMLDivElement>(null);

	const [validateClassCode, { isLoading: isValidating }] =
		useValidateClassCodeMutation();
	const [verifyIdentity, { isLoading: isVerifying }] =
		useVerifyIdentityMutation();
	const [studentSetPin, { isLoading: isSettingPin }] =
		useStudentSetPinMutation();
	const [studentLogin, { isLoading: isLoggingIn }] = useStudentLoginMutation();
	const [faceLogin, { isLoading: isFaceLoggingIn }] =
		useStudentFaceLoginMutation();

	const { data: schools = [], isLoading: isLoadingSchools } =
		useListPublicSchoolsQuery();
	const [fetchClasses, { data: classes = [], isLoading: isLoadingClasses }] =
		useLazyListPublicClassesQuery();

	const loading = isValidating || isVerifying || isSettingPin || isLoggingIn;

	useEffect(() => {
		if (savedSchoolId) fetchClasses(savedSchoolId);
	}, [fetchClasses, savedSchoolId]);

	const schoolQuery = schoolSearch.trim();
	const showSchoolResults = schoolQuery.length >= 2;
	const filteredSchools = showSchoolResults
		? schools.filter((s) =>
				s.name.toLowerCase().startsWith(schoolQuery.toLowerCase()),
			)
		: [];

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (schoolRef.current && !schoolRef.current.contains(e.target as Node)) {
				setSchoolDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const formatClassCode = (raw: string) => {
		const s = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase();
		let r = '';
		for (let i = 0; i < s.length; i++) {
			if (i === 2 || i === 4 || i === 6 || i === 12) r += '-';
			r += s[i];
		}
		return r;
	};

	const formatAlforaaId = (raw: string) => {
		const stripped = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase();
		let result = '';
		let alphaCount = 0;
		for (let i = 0; i < stripped.length; i++) {
			const ch = stripped[i];
			if (alphaCount === 2 || alphaCount === 4 || alphaCount === 6) {
				if (result[result.length - 1] !== '-') result += '-';
			}
			if (alphaCount === 12) {
				if (result[result.length - 1] !== '/') result += '/';
			}
			result += ch;
			alphaCount++;
		}
		return result;
	};

	const handleClassCode = async () => {
		setError('');
		analytics.student.loginClassCodeSubmitted(classCode.trim());
		try {
			const result = await validateClassCode({
				classCode: classCode.trim(),
			}).unwrap();
			setStudentData(result);
			setStep(STEPS.VERIFY);
		} catch (e: any) {
			const reason = e?.data?.detail || 'Invalid class code';
			setError(reason);
			analytics.student.loginClassCodeFailed(reason);
		}
	};

	const handleVerifyIdentity = async () => {
		setError('');
		try {
			const result = await verifyIdentity({
				classCode: classCode.trim(),
				name: name.trim(),
				rollNumber: rollNumber.trim(),
			}).unwrap();
			setStudentId(result.id);
			analytics.student.loginIdentityVerified();
			if (result.firstVisitCompleted && result.alforaaId) {
				setAlforaaId(result.alforaaId);
				setStep(STEPS.LOGIN);
			} else {
				setStep(STEPS.SET_PIN);
			}
		} catch (e: any) {
			const reason = e?.data?.detail || 'Student not found';
			setError(reason);
			analytics.student.loginIdentityFailed(reason);
		}
	};

	const handleSetPin = async (pinValue: string) => {
		setPinError('');
		try {
			const result = await studentSetPin({ studentId, pin: pinValue }).unwrap();
			analytics.student.loginPinSetCompleted();
			setAlforaaId(result.alforaaId);
			dispatch(
				setCredentials({
					user: result.user,
					role: 'student',
					accessToken: result.accessToken,
					refreshToken: result.refreshToken,
				}),
			);
			setStep(STEPS.REVEAL_ID);
		} catch (e: any) {
			setPinError(e?.data?.detail || 'Failed to set PIN');
		}
	};

	const handleLogin = async (pinValue: string) => {
		setPinError('');
		analytics.student.loginPinSubmitted();
		try {
			const result = await studentLogin({
				alforaaId: alforaaId.trim(),
				pin: pinValue,
			}).unwrap();
			identifyUser(result.user, 'student');
			analytics.student.loginSucceeded('pin');
			dispatch(
				setCredentials({
					user: result.user,
					role: 'student',
					accessToken: result.accessToken,
					refreshToken: result.refreshToken,
				}),
			);
			navigate('/student/dashboard');
		} catch (e: any) {
			const reason = (e as any)?.data?.detail || 'Incorrect PIN';
			setPinError(reason);
			analytics.student.loginPinFailed(reason);
		}
	};

	const copyId = () => {
		navigator.clipboard.writeText(alforaaId);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const handleSchoolSelect = (schoolId: string, schoolName: string) => {
		setSelectedSchoolId(schoolId);
		setSelectedSchoolName(schoolName);
		setSelectedClassId('');
		setSelectedClassCode('');
		setShowFaceScan(false);
		setFaceError('');
		if (schoolId) {
			localStorage.setItem('faceLoginSchoolId', schoolId);
			localStorage.setItem('faceLoginSchoolName', schoolName);
			fetchClasses(schoolId);
		}
	};

	const handleClassSelect = (classId: string, classCode: string) => {
		setSelectedClassId(classId);
		setSelectedClassCode(classCode);
		setShowFaceScan(false);
		setFaceError('');
	};

	const handleFaceLogin = async (descriptor: number[]) => {
		setFaceError('');
		analytics.student.loginFaceIdAttempted();
		try {
			const result = await faceLogin({
				schoolId: selectedSchoolId,
				classId: selectedClassId,
				faceDescriptor: descriptor,
			}).unwrap();
			identifyUser(result.user, 'student');
			analytics.student.loginFaceIdSucceeded();
			analytics.student.loginSucceeded('face');
			dispatch(
				setCredentials({
					user: result.user,
					role: 'student',
					accessToken: result.accessToken,
					refreshToken: result.refreshToken,
				}),
			);
			navigate('/student/dashboard');
		} catch (e: any) {
			const status = e?.status;
			const detail = e?.data?.detail;
			const reason =
				status === 401
					? 'Face not recognized. Please try again.'
					: detail || 'Face login failed. Please try again.';
			setFaceError(reason);
			analytics.student.loginFaceIdFailed(reason);
		}
	};

	const canScanFace = selectedSchoolId && selectedClassId;

	return (
		<div
			className='min-h-screen aurora-bg flex flex-col lg:flex-row items-stretch relative overflow-hidden'
			data-testid='student-login-page'>
			{/* glow blobs */}
			<div
				aria-hidden
				className='pointer-events-none absolute top-[-100px] left-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(255,180,140,0.55), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>
			<div
				aria-hidden
				className='pointer-events-none absolute bottom-[-100px] right-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(220,200,255,0.6), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>

			{/* Mobile/tablet: compact logo header */}
			<div className='lg:hidden flex items-center justify-center pt-8 pb-2 px-6 relative z-10'>
				<img
					src={alforaaFullLogoDark}
					alt='Alforaa'
					className='h-9 w-auto block'
				/>
			</div>

			{/* Left visual pane (desktop only) */}
			<div className='hidden lg:flex flex-1 flex-col justify-between p-14 relative z-10'>
				<div className='flex items-center'>
					<img
						src={alforaaFullLogoDark}
						alt='Alforaa'
						className='h-10 w-auto block'
					/>
				</div>
				<div>
					<div className='text-[14px] text-aurora-coral font-semibold uppercase tracking-[0.04em]'>
						Designed for curious minds
					</div>
					<div className='text-[56px] font-semibold tracking-[-0.04em] text-aurora-ink leading-[1.05] mt-3'>
						Hands-on science,
						<br />
						built for{' '}
						<span
							style={{
								background: 'linear-gradient(135deg, #FF7A45, #E8541F)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}>
							every classroom.
						</span>
					</div>
					<div className='text-[16px] text-aurora-ink-soft leading-relaxed mt-4 max-w-[460px]'>
						Curriculum-aligned experiments, interactive simulations, and lab
						kits — delivered to your school. Learn by doing, track every step,
						and master concepts that stick.
					</div>
				</div>
				<div className='flex gap-5 items-center'>
					<div className='flex'>
						{[
							'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=160&h=160&fit=crop&crop=faces&q=80',
						].map((src, i) => (
							<img
								key={i}
								src={src}
								alt=''
								className='w-9 h-9 rounded-full border-2 border-white object-cover bg-aurora-coral-soft'
								style={{ marginLeft: i === 0 ? 0 : -10 }}
							/>
						))}
					</div>
					<div className='text-[12px] text-aurora-ink-soft'>
						<div className='font-semibold text-aurora-ink'>
							Loved by students across our partner schools
						</div>
						<div>Real labs, real outcomes, real curiosity.</div>
					</div>
				</div>
			</div>

			{/* Right login card */}
			<div className='flex-1 flex items-center justify-center relative z-10 p-6 sm:p-10 lg:p-0'>
				<div
					className='bg-white/95 rounded-[28px] p-7 sm:p-10 w-full max-w-[480px] animate-fade-in border border-aurora-ink/[0.06]'
					style={{
						boxShadow:
							'0 20px 60px -20px rgba(15,18,53,0.18), 0 8px 24px -12px rgba(15,18,53,0.08)',
					}}>
					<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
						Welcome back
					</div>
					<div className='text-[13px] text-aurora-ink-mute mt-1'>
						Sign in to continue to your dashboard
					</div>

					{step === STEPS.LOGIN && (
						<div className='flex gap-1 mt-5 p-1 bg-[rgba(15,18,53,0.05)] rounded-[12px]'>
							<button
								data-track='student_login_switch_to_face_clicked'
								onClick={() => {
									setTab('face');
									if (selectedSchoolId) fetchClasses(selectedSchoolId);
								}}
								className={`flex-1 py-2 text-[12px] font-semibold rounded-[9px] flex items-center justify-center gap-1.5 transition-all ${
									tab === 'face'
										? 'bg-white text-aurora-ink shadow-[0_1px_4px_rgba(15,18,53,0.08)]'
										: 'text-aurora-ink-mute'
								}`}>
								<ScanFace size={14} /> Face ID
							</button>
							<button
								data-track='student_login_switch_to_pin_clicked'
								onClick={() => setTab('pin')}
								className={`flex-1 py-2 text-[12px] font-semibold rounded-[9px] transition-all ${
									tab === 'pin'
										? 'bg-white text-aurora-ink shadow-[0_1px_4px_rgba(15,18,53,0.08)]'
										: 'text-aurora-ink-mute'
								}`}>
								ID & PIN
							</button>
						</div>
					)}

					{/* PIN tab */}
					{tab === 'pin' && (
						<>
							{step === STEPS.LOGIN && (
								<div className='space-y-5 mt-5'>
									<div>
										<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
											Alforaa ID
										</label>
										<AuroraInput
											value={alforaaId}
											onChange={(e: any) => {
												const formatted = formatAlforaaId(e.target.value);
												setAlforaaId(formatted);
												const slashIdx = formatted.indexOf('/');
												if (slashIdx !== -1)
													localStorage.setItem(
														'studentIdPrefix',
														formatted.slice(0, slashIdx),
													);
											}}
											placeholder='Enter your Alforaa ID'
											data-testid='student-alforaa-id-input'
											disabled={isLoggingIn}
											loading={isLoggingIn}
										/>
									</div>
									{alforaaId && (
										<div className='relative'>
											<PinPad
												onComplete={handleLogin}
												error={pinError}
												label='Enter your PIN'
												testIdPrefix='student-login-pin'
											/>
											{isLoggingIn && (
												<div className='absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm rounded-[16px] gap-3'>
													<ButtonSpinner />
													<div className='text-[13px] font-medium text-aurora-ink'>
														Signing you in…
													</div>
													<div className='text-[11.5px] text-aurora-ink-mute'>
														Hang on, this can take a moment.
													</div>
												</div>
											)}
										</div>
									)}
									{error && (
										<p
											className='text-status-error text-[12px] text-center'
											data-testid='login-error'>
											{error}
										</p>
									)}
									<div className='text-center pt-1'>
										<button
											data-track='student_login_back_to_class_code_clicked'
											onClick={() => {
												setStep(STEPS.CLASS_CODE);
												setError('');
											}}
											data-testid='first-time-setup-btn'
											className='text-[12.5px] text-aurora-coral font-semibold hover:underline'>
											First time? Set up your account
										</button>
									</div>
								</div>
							)}

							{step === STEPS.CLASS_CODE && (
								<div className='space-y-4 mt-5'>
									<h2 className='text-[16px] font-semibold text-aurora-ink text-center'>
										Enter your class code
									</h2>
									<p className='text-[12px] text-aurora-ink-mute text-center'>
										Ask your trainer for the class code
									</p>
									<AuroraInput
										value={classCode}
										onChange={(e: any) =>
											setClassCode(formatClassCode(e.target.value))
										}
										placeholder='Class code'
										data-testid='class-code-input'
										disabled={isValidating}
										loading={isValidating}
										className='text-center uppercase tracking-wider'
									/>
									{error && (
										<p className='text-status-error text-[12px] text-center'>
											{error}
										</p>
									)}
									<button
										onClick={handleClassCode}
										disabled={!classCode.trim() || loading}
										data-testid='validate-class-code-btn'
										className='w-full py-3 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2'>
										{loading && <ButtonSpinner />}
										{loading ? 'Validating…' : 'Continue'}
										{!loading && <ArrowRight size={16} />}
									</button>
									<button
										onClick={() => setStep(STEPS.LOGIN)}
										className='w-full text-[12.5px] text-aurora-ink-mute hover:text-aurora-coral text-center'>
										Already have an Alforaa ID? Login
									</button>
								</div>
							)}

							{step === STEPS.VERIFY && (
								<div className='space-y-4 mt-5'>
									<h2 className='text-[16px] font-semibold text-aurora-ink text-center'>
										Verify your identity
									</h2>
									{studentData && (
										<p className='text-[12px] text-aurora-ink-mute text-center'>
											{studentData.schoolName} — {studentData.classCode}
										</p>
									)}
									<div>
										<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
											Full name
										</label>
										<AuroraInput
											value={name}
											onChange={(e: any) => setName(e.target.value)}
											placeholder='Enter your full name'
											data-testid='student-name-input'
											disabled={isVerifying}
											loading={isVerifying}
										/>
									</div>
									<div>
										<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
											Roll number
										</label>
										<AuroraInput
											value={rollNumber}
											onChange={(e: any) => setRollNumber(e.target.value)}
											placeholder='Enter your roll number'
											data-testid='student-roll-input'
											disabled={isVerifying}
											loading={isVerifying}
										/>
									</div>
									{error && (
										<p className='text-status-error text-[12px] text-center'>
											{error}
										</p>
									)}
									<button
										onClick={handleVerifyIdentity}
										disabled={!name.trim() || !rollNumber.trim() || loading}
										data-testid='verify-identity-btn'
										className='w-full py-3 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral disabled:opacity-50 flex items-center justify-center gap-2'>
										{loading && <ButtonSpinner />}
										{loading ? 'Verifying…' : 'Verify & continue'}
									</button>
								</div>
							)}

							{step === STEPS.SET_PIN && (
								<div className='space-y-4 mt-5'>
									<h2 className='text-[16px] font-semibold text-aurora-ink text-center'>
										Create your PIN
									</h2>
									<p className='text-[12px] text-aurora-ink-mute text-center'>
										Choose a 4-digit PIN. You'll use this to log in.
									</p>
									<div className='relative'>
										<PinPad
											onComplete={handleSetPin}
											error={pinError}
											label='Set your 4-digit PIN'
											testIdPrefix='student-set-pin'
										/>
										{isSettingPin && (
											<div className='absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm rounded-[16px] gap-3'>
												<ButtonSpinner />
												<div className='text-[13px] font-medium text-aurora-ink'>
													Creating your account…
												</div>
												<div className='text-[11.5px] text-aurora-ink-mute'>
													This only takes a moment.
												</div>
											</div>
										)}
									</div>
								</div>
							)}

							{step === STEPS.REVEAL_ID && (
								<div className='space-y-5 text-center mt-5'>
									<CheckCircle size={48} className='text-aurora-mint mx-auto' />
									<h2 className='text-[20px] font-semibold text-aurora-ink'>
										Your Alforaa ID
									</h2>
									<p className='text-[13px] text-aurora-ink-mute'>
										Save this ID. You'll use it to log in every time.
									</p>
									<div
										className='rounded-[16px] p-5 border-2'
										style={{
											background: 'rgba(255,228,214,0.6)',
											borderColor: 'rgba(255,122,69,0.5)',
										}}
										data-testid='alforaa-id-display'>
										<p className='text-[28px] text-aurora-coral-deep font-bold tracking-wider'>
											{alforaaId}
										</p>
									</div>
									<button
										onClick={copyId}
										data-testid='copy-alforaa-id-btn'
										className='flex items-center gap-2 mx-auto text-[12.5px] text-aurora-coral font-semibold hover:underline'>
										<Copy size={14} /> {copied ? 'Copied!' : 'Copy ID'}
									</button>
									<button
										onClick={() => navigate('/student/dashboard')}
										data-testid='go-to-dashboard-btn'
										className='w-full py-3 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral'>
										Go to dashboard
									</button>
								</div>
							)}
						</>
					)}

					{/* Face tab */}
					{tab === 'face' && step === STEPS.LOGIN && (
						<div className='space-y-4 mt-5'>
							<div>
								<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
									School
								</label>
								<div ref={schoolRef} className='relative'>
									<AuroraInput
										value={schoolSearch}
										onChange={(e: any) => {
											setSchoolSearch(e.target.value);
											setSchoolDropdownOpen(true);
											if (!e.target.value.trim()) {
												handleSchoolSelect('', '');
											}
										}}
										onFocus={() => setSchoolDropdownOpen(true)}
										placeholder='Type your school name…'
										disabled={isLoadingSchools}
										loading={isLoadingSchools}
										leftIcon={<Search size={14} />}
										autoComplete='off'
									/>
									{schoolDropdownOpen && showSchoolResults && (
										<div className='absolute z-50 w-full mt-1 bg-white rounded-[12px] max-h-56 overflow-y-auto border border-aurora-ink/10 shadow-[0_10px_30px_-8px_rgba(15,18,53,0.18)]'>
											{filteredSchools.length === 0 ? (
												<div className='px-4 py-3 text-[12.5px] text-aurora-ink-mute'>
													No schools match "{schoolQuery}".
												</div>
											) : (
												filteredSchools.map((s) => (
													<button
														key={s.id}
														type='button'
														onMouseDown={(e) => {
															e.preventDefault();
															handleSchoolSelect(s.id, s.name);
															setSchoolSearch(s.name);
															setSchoolDropdownOpen(false);
														}}
														className={`w-full px-4 py-2.5 text-left hover:bg-aurora-coral/10 transition-colors ${
															selectedSchoolId === s.id
																? 'bg-aurora-coral/5'
																: ''
														}`}>
														<div
															className={`text-[13px] leading-snug ${selectedSchoolId === s.id ? 'text-aurora-coral font-semibold' : 'text-aurora-ink font-medium'}`}>
															{s.name}
															{s.city && (
																<span className='text-aurora-ink-mute font-normal'>
																	, {s.city}
																</span>
															)}
														</div>
													</button>
												))
											)}
										</div>
									)}
								</div>
							</div>

							{selectedSchoolId && (
								<div>
									<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
										Class
									</label>
									<Popover
										open={classComboOpen}
										onOpenChange={setClassComboOpen}>
										<PopoverTrigger asChild>
											<button
												type='button'
												role='combobox'
												aria-expanded={classComboOpen}
												disabled={isLoadingClasses}
												className='w-full flex items-center justify-between px-4 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 text-[14px] text-left shadow-[0_1px_2px_rgba(15,18,53,0.05)] transition-all disabled:bg-[#F4F4F8] disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-aurora-coral/30 focus:border-aurora-coral'>
												<span
													className={
														selectedClassId
															? 'text-aurora-ink font-medium'
															: isLoadingClasses
																? 'text-aurora-ink-mute'
																: 'text-aurora-ink-faint'
													}>
													{isLoadingClasses
														? 'Loading classes…'
														: selectedClassId
															? (() => {
																	const c = classes.find(
																		(x) => x.id === selectedClassId,
																	);
																	return c
																		? `Grade ${c.classNumber} — ${c.schoolSection || c.classCode}`
																		: 'Select class…';
																})()
															: 'Select class…'}
												</span>
												{isLoadingClasses ? (
													<span
														className='inline-block w-4 h-4 rounded-full animate-spin shrink-0 ml-2'
														style={{
															border: '2px solid rgba(232,84,31,0.25)',
															borderTopColor: '#E8541F',
														}}
													/>
												) : (
													<ChevronsUpDown
														size={16}
														className='text-aurora-ink-faint shrink-0 ml-2'
													/>
												)}
											</button>
										</PopoverTrigger>
										<PopoverContent
											className='p-0 aurora-glass-strong'
											align='start'
											style={{ width: 'var(--radix-popper-anchor-width)' }}>
											<Command>
												<CommandInput placeholder='Search class…' />
												<CommandList>
													<CommandEmpty>No classes found.</CommandEmpty>
													{Array.from(
														new Set(classes.map((c) => c.classNumber)),
													)
														.sort((a, b) => Number(a) - Number(b))
														.map((grade) => (
															<CommandGroup
																key={grade}
																heading={`Grade ${grade}`}>
																{classes
																	.filter((c) => c.classNumber === grade)
																	.map((c) => (
																		<CommandItem
																			key={c.id}
																			value={`${grade} ${c.schoolSection || c.classCode}`}
																			onSelect={() => {
																				handleClassSelect(c.id, c.classCode);
																				setClassComboOpen(false);
																			}}>
																			<Check
																				size={14}
																				className={`mr-2 shrink-0 ${selectedClassId === c.id ? 'opacity-100' : 'opacity-0'}`}
																			/>
																			{c.schoolSection || c.classCode}
																		</CommandItem>
																	))}
															</CommandGroup>
														))}
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
								</div>
							)}

							{faceError && (
								<p className='text-status-error text-[12px] text-center'>
									{faceError}
								</p>
							)}

							{!showFaceScan ? (
								<button
									data-track='student_login_face_id_attempted'
									onClick={() => {
										setFaceError('');
										setShowFaceScan(true);
									}}
									disabled={!canScanFace || isFaceLoggingIn}
									data-testid='face-scan-btn'
									className='w-full py-3 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2'>
									<ScanFace size={18} />
									{canScanFace ? 'Scan face' : 'Select school & class first'}
								</button>
							) : (
								<div className='space-y-3'>
									<FaceCapture
										onCapture={handleFaceLogin}
										onError={(msg) => {
											setFaceError(msg);
											analytics.student.loginFaceIdFailed(msg);
										}}
									/>
									<button
										onClick={() => {
											setShowFaceScan(false);
											setFaceError('');
										}}
										className='w-full text-[12.5px] text-aurora-ink-mute hover:text-aurora-coral text-center'>
										Cancel
									</button>
								</div>
							)}

							<div className='text-center pt-1'>
								<button
									data-track='student_login_use_pin_instead_clicked'
									onClick={() => {
										setTab('pin');
										setFaceError('');
									}}
									className='text-[12.5px] text-aurora-coral font-semibold hover:underline'>
									Use ID & PIN instead
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
